
import React, { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useBranch } from '../contexts/BranchContext';
import { useLanguage, SUPPORTED_LANGUAGES } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { addToQueue, saveToStore, syncStoreWithServer, getAllFromStore, removeFromQueue, getQueue } from '../utils/localDb';


// Safe notification checks for iOS/Safari support
const hasNotificationSupport = typeof window !== 'undefined' && 'Notification' in window;

function KitchenPage() {
    const { t, language } = useLanguage();
    const navigate = useNavigate();
    const { formatAmount } = useCurrency();
    const [orders, setOrders] = useState([]);
    // const [currentFilter, setCurrentFilter] = useState('pending');
    const [connectionStatus, setConnectionStatus] = useState(
        navigator.onLine ? 'Connecting...' : '🟡 Offline Mode'
    );

    const [notificationPermission, setNotificationPermission] = useState(
        hasNotificationSupport ? Notification.permission : 'denied'
    );
    const [showOnlineOrders, setShowOnlineOrders] = useState(false);

    // Manual Online Order Entry Modal state
    const [showAddOnlineModal, setShowAddOnlineModal] = useState(false);
    const [addOnlineForm, setAddOnlineForm] = useState({ platform: 'swiggy', customerName: '', orderRef: '', notes: '', items: [] });
    const [menuItemsList, setMenuItemsList] = useState([]);
    const [addOnlineLoading, setAddOnlineLoading] = useState(false);
    const [addOnlineError, setAddOnlineError] = useState('');
    const [menuLoadingForModal, setMenuLoadingForModal] = useState(false);

    // Toast state and helper for on-screen notification popups
    const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
    const toastTimeoutRef = useRef(null);
    const showToast = useCallback((message, type = 'info') => {
        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
        }
        setToast({ show: true, message, type });
        toastTimeoutRef.current = setTimeout(() => {
            setToast({ show: false, message: '', type: 'info' });
            toastTimeoutRef.current = null;
        }, 5000);
    }, []);

    // Robust date parsing for cross-browser/iOS compatibility
    const parseDateSafe = (dateStr) => {
        if (!dateStr) return new Date();
        let normalized = dateStr;
        if (typeof normalized === 'string') {
            normalized = normalized.trim().replace(/\s+/, 'T');
        }
        const parsed = new Date(normalized);
        if (isNaN(parsed.getTime())) {
            return new Date(dateStr);
        }
        return parsed;
    };

    const isOnlineOrder = (order) => {
        const method = (order.payment_method || '').toLowerCase();
        return method === 'swiggy' || method === 'zomato';
    };

    const pendingOnlineCount = orders.filter(o => o.order_status === 'pending' && isOnlineOrder(o)).length;
    const pendingDineInCount = orders.filter(o => o.order_status === 'pending' && !isOnlineOrder(o)).length;

    const { token, logout, currentUser, isLoading: authLoading } = useAuth();
    const [deliveredDateFilter, setDeliveredDateFilter] = useState('today');
    const [companyInfo, setCompanyInfo] = useState(null);
    const { branches } = useBranch();
    const [selectedBranch, setSelectedBranchState] = useState(() => {
        const saved = localStorage.getItem('selectedBranch_kitchen');
        return saved ? (saved === 'null' ? null : parseInt(saved, 10)) : null;
    });
    const setSelectedBranch = (val) => {
        setSelectedBranchState(val);
        localStorage.setItem('selectedBranch_kitchen', val === null ? 'null' : val.toString());
    };
    // Auto-select the only branch when there's exactly one
    useEffect(() => {
        if (branches && branches.length === 1 && !selectedBranch) {
            setSelectedBranch(branches[0].id);
        }
    }, [branches]); // eslint-disable-line react-hooks/exhaustive-deps
    // const { language, changeLanguage } = useLanguage();
    const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
    const [showBranchDropdown, setShowBranchDropdown] = useState(false);
    const [showBillModal, setShowBillModal] = useState(false);
    const [billData, setBillData] = useState(null);
    const [draggedOrder, setDraggedOrder] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const fetchCompanyInfo = async () => {
            if (authLoading) return;
            try {
                const API_URL = (() => {
                    const hostname = window.location.hostname;
                    const isLocalIP = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.') || hostname.startsWith('10.') || /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname);
                    return isLocalIP ? `http://${hostname}:5000` : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');
                })();

                let url = `${API_URL}/api/company/public`;
                let options = {};

                if (token) {
                    url = `${API_URL}/api/company/profile`;
                    options = {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    };
                }

                const res = await fetch(url, options);
                const json = await res.json();
                if (isMounted && json.success && json.data) {
                    setCompanyInfo(json.data);
                }
            } catch (err) {
                console.error("Failed to fetch company info", err);
            }
        };
        fetchCompanyInfo();
        return () => { isMounted = false; };
    }, [token, authLoading]);

    const audioRef = useRef(null);
    const API_URL = (() => {
        const hostname = window.location.hostname;
        const isLocalIP = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.') || hostname.startsWith('10.') || /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname);
        return isLocalIP ? `http://${hostname}:5000` : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');
    })();

    const useCurrentTimer = () => {
        const [now, setNow] = useState(Date.now());
        useEffect(() => {
            const interval = setInterval(() => setNow(Date.now()), 1000);
            return () => clearInterval(interval);
        }, []);
        return now;
    };

    const currentTime = useCurrentTimer();

    const getElapsedTimeParts = (createdAt) => {
        const created = parseDateSafe(createdAt).getTime();
        const diff = currentTime - created;
        const mins = Math.floor(diff / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        return { mins, secs, totalMins: mins };
    };

    const playNotificationSound = () => {
        if (audioRef.current) {
            audioRef.current.play().catch(e => console.log('Audio play failed:', e));
        }
    };

    const getDateRange = (filter) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);

        switch (filter) {
            case 'today': return { start: today, end: tomorrow };
            case 'yesterday': return { start: yesterday, end: today };
            case 'week': return { start: weekAgo, end: tomorrow };
            case 'month': return { start: monthAgo, end: tomorrow };
            default: return { start: today, end: tomorrow };
        }
    };

    const openAddOnlineModal = async () => {
        setAddOnlineForm({ platform: 'swiggy', customerName: '', orderRef: '', notes: '', items: [] });
        setAddOnlineError('');
        setShowAddOnlineModal(true);
        setMenuLoadingForModal(true);
        try {
            let menuUrl = `${API_URL}/api/menu`;
            if (selectedBranch) menuUrl += `?branch_id=${selectedBranch}`;
            const menuRes = await fetch(menuUrl, { headers: { 'Authorization': `Bearer ${token}` } });
            const menuData = await menuRes.json();
            if (menuData.success && menuData.data) {
                const valid = menuData.data.filter(item => item.is_available !== false);
                setMenuItemsList(valid);
            }
        } catch (e) {
            console.error('Failed to load menu for online order modal', e);
            // OFFLINE FALLBACK
            try {
                const cachedMenu = await getAllFromStore('menu');
                if (cachedMenu && cachedMenu.length > 0) {
                    const valid = cachedMenu.filter(item => item.is_available !== false);
                    setMenuItemsList(valid);
                }
            } catch (dbErr) {
                console.error('IndexedDB menu fetch error:', dbErr);
            }
        }
        setMenuLoadingForModal(false);
    };

    const toggleModalItem = (item) => {
        setAddOnlineForm(prev => {
            const existing = prev.items.find(i => i.id === item.id);
            if (existing) {
                return { ...prev, items: prev.items.filter(i => i.id !== item.id) };
            } else {
                return { ...prev, items: [...prev.items, { ...item, qty: 1 }] };
            }
        });
    };

    const changeModalItemQty = (itemId, delta) => {
        setAddOnlineForm(prev => ({
            ...prev,
            items: prev.items.map(i => i.id === itemId ? { ...i, qty: Math.max(1, (i.qty || 1) + delta) } : i)
        }));
    };

    const submitManualOnlineOrder = async () => {
        if (addOnlineForm.items.length === 0) {
            setAddOnlineError('Please select at least one item.');
            return;
        }
        setAddOnlineLoading(true);
        setAddOnlineError('');

        // Calculate totals for DB compatibility
        let total_inr = 0;
        let total_usd = 0;
        addOnlineForm.items.forEach(item => {
            const qty = parseInt(item.qty || 1);
            total_inr += parseFloat(item.price_inr || item.price || 0) * qty;
            total_usd += parseFloat(item.price_usd || 0) * qty;
        });

        const orderData = {
            table_number: 0,
            is_waiting: false,
            items: addOnlineForm.items.map(i => ({
                id: i.id,
                menu_item_id: i.id,
                item_name: i.name,
                name: i.name,
                quantity: i.qty || 1,
                price: i.price_inr || i.price || 0,
                price_inr: i.price_inr,
                price_usd: i.price_usd,
                discount_percentage: 0,
                item_status: 'pending'
            })),
            currency: 'INR',
            payment_method: addOnlineForm.platform,
            customer_name: addOnlineForm.customerName || `${addOnlineForm.platform.charAt(0).toUpperCase() + addOnlineForm.platform.slice(1)} Order`,
            notes: addOnlineForm.orderRef ? `Order Ref: ${addOnlineForm.orderRef}${addOnlineForm.notes ? ' | ' + addOnlineForm.notes : ''}` : addOnlineForm.notes,
            branch_id: selectedBranch,
            total_amount_inr: total_inr.toFixed(2),
            total_amount_usd: total_usd.toFixed(2)
        };
        
        // Always attempt the server first for LAN-based servers
        try {
            const response = await fetch(`${API_URL}/api/orders`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(orderData)
            });
            const data = await response.json();
            if (data.success) {
                setShowAddOnlineModal(false);
                showToast(`New ${addOnlineForm.platform} order added to kitchen!`, 'success');
                return;
            }
            console.warn('[KitchenPage] Server rejected order, queuing locally:', data.message);
        } catch (err) {
            console.warn('[KitchenPage] Server unreachable, queuing order locally:', err.message);
        }

        // ── Offline fallback ──────────────────────────────────────────────────
        try {
            const queuedOrder = await addToQueue(orderData);
            setOrders(prev => {
                if (prev.some(o => o.id === queuedOrder.id)) return prev;
                return [queuedOrder, ...prev];
            });
            setShowAddOnlineModal(false);
            showToast(`No connection to server. ${addOnlineForm.platform} order saved offline!`, 'warning');
            
            // Broadcast local event for tab KDS sync
            try {
                const channel = new BroadcastChannel('redsorm_sync_channel');
                channel.postMessage({ type: 'NEW_OFFLINE_ORDER', data: queuedOrder });
                setTimeout(() => { try { channel.close(); } catch (e) {} }, 1000);
            } catch (bcErr) {
                console.warn('Broadcast Channel error:', bcErr);
            }
        } catch (localErr) {
            console.error('[IndexedDB] Failed to save offline order:', localErr);
            setAddOnlineError('Failed to save order offline.');
        } finally {
            setAddOnlineLoading(false);
        }
    };

    const loadOrders = useCallback(async () => {
        try {
            let url = `${API_URL}/api/orders`;
            if (selectedBranch) url += `?branch_id=${selectedBranch}&_t=${Date.now()}`;
            else url += `?_t=${Date.now()}`;
            
            const response = await fetch(url, {
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.status === 401) {
                logout();
                return;
            }
            if (response.status === 403) {
                return;
            }
            const data = await response.json();
            if (data.success) {
                const rawOrders = data.data || [];

                // Always merge any pending offline orders from the queue
                // This covers: orders placed on this device before server was reachable
                try {
                    const queuedOrders = await getQueue();
                    if (queuedOrders && queuedOrders.length > 0) {
                        queuedOrders.forEach(qOrder => {
                            // Only add if not already present (not yet synced to server)
                            const alreadySynced = rawOrders.some(
                                o => String(o.id) === String(qOrder.id) || String(o.id) === String(qOrder.order_id)
                            );
                            if (!alreadySynced) {
                                rawOrders.push({
                                    ...qOrder,
                                    order_status: qOrder.order_status || 'pending',
                                    items: (qOrder.items || []).map(item => ({
                                        ...item,
                                        item_name: item.item_name || item.name || '',
                                        item_status: item.item_status || 'pending'
                                    }))
                                });
                            }
                        });
                    }
                } catch (qErr) {
                    console.warn('[KDS] Could not merge offline queue with server orders:', qErr);
                }

                const sortedOrders = rawOrders.sort((a, b) => {
                    if (a.is_waiting !== b.is_waiting) {
                        return a.is_waiting ? 1 : -1;
                    }
                    return new Date(a.created_at) - new Date(b.created_at);
                });
                // Use functional update: preserve any offline orders already in state that our
                // getQueue() snapshot may have missed due to a cross-tab IndexedDB write race.
                // This is the key fix for orders that appear via broadcast then immediately vanish.
                setOrders(prev => {
                    const missedOfflineOrders = prev.filter(po =>
                        po.is_offline &&
                        !sortedOrders.some(s =>
                            String(s.id) === String(po.id) ||
                            (po.order_id && String(s.id) === String(po.order_id)) ||
                            (s.order_id && String(s.order_id) === String(po.id))
                        )
                    );
                    if (missedOfflineOrders.length === 0) return sortedOrders;
                    const merged = [...sortedOrders, ...missedOfflineOrders];
                    return merged.sort((a, b) => {
                        if (a.is_waiting !== b.is_waiting) return a.is_waiting ? 1 : -1;
                        return new Date(a.created_at) - new Date(b.created_at);
                    });
                });
                // Sync orders to IndexedDB
                syncStoreWithServer('orders', rawOrders.filter(o => !o.is_offline)).catch(err => console.error('[IndexedDB] Sync orders failed:', err));
            }
        } catch (error) {
            console.error('Error loading orders:', error);
            // OFFLINE FALLBACK - Load both cached online orders and queued offline orders
            try {
                const cachedOrders = await getAllFromStore('orders');
                const queuedOrders = await getAllFromStore('orders_queue');

                // Merge them, prioritizing queued orders
                const mergedOrders = [...queuedOrders.map(qOrder => ({
                    ...qOrder,
                    order_status: qOrder.order_status || 'pending',
                    items: (qOrder.items || []).map(item => ({
                        ...item,
                        item_name: item.item_name || item.name || '',
                        item_status: item.item_status || 'pending'
                    }))
                }))];
                cachedOrders.forEach(co => {
                    if (!mergedOrders.some(qo => qo.id === co.id || qo.order_id === co.id)) {
                        mergedOrders.push(co);
                    }
                });

                const sortedOrders = mergedOrders.sort((a, b) => {
                    if (a.is_waiting !== b.is_waiting) {
                        return a.is_waiting ? 1 : -1;
                    }
                    return new Date(a.created_at) - new Date(b.created_at);
                });

                const filteredOrders = selectedBranch
                    ? sortedOrders.filter(o => String(o.branch_id) === String(selectedBranch) || o.branch_id === null || o.branch_id === undefined)
                    : sortedOrders;

                // Use functional update: also keep offline orders from current state
                // that aren't yet in IndexedDB (e.g., just added via broadcast in same render cycle)
                setOrders(prev => {
                    const merged = [...filteredOrders];
                    prev.forEach(po => {
                        if (po.is_offline && !merged.some(m => String(m.id) === String(po.id))) {
                            merged.push(po);
                        }
                    });
                    return merged.sort((a, b) => {
                        if (a.is_waiting !== b.is_waiting) return a.is_waiting ? 1 : -1;
                        return new Date(a.created_at) - new Date(b.created_at);
                    });
                });
            } catch (dbErr) {
                console.error('[IndexedDB] Load orders failed offline:', dbErr);
                showToast(t('serverError') || 'Network error loading orders', 'error');
            }
        }
    }, [API_URL, token, logout, selectedBranch, t, showToast]);

    // eslint-disable-next-line no-unused-vars
    const generateBillForTable = (tableNumber) => {
        const tableOrders = orders.filter(o =>
            o.table_number === tableNumber && o.order_status !== 'cancelled'
        );
        if (tableOrders.length === 0) return;

        let subtotal = 0;
        tableOrders.forEach(order => {
            if (order.items) {
                order.items.forEach(item => {
                    if (item.item_status !== 'cancelled') {
                        const price = parseFloat(item.price || item.price_inr || 0);
                        const qty = parseInt(item.quantity || 1);
                        subtotal += price * qty;
                    }
                });
            } else {
                subtotal += parseFloat(order.total_amount || order.total_amount_inr || 0);
            }
        });

        const sgst = parseFloat(companyInfo?.sgst || 0);
        const cgst = parseFloat(companyInfo?.cgst || 0);
        const sgstAmount = subtotal * (sgst / 100);
        const cgstAmount = subtotal * (cgst / 100);
        const total = subtotal + sgstAmount + cgstAmount;

        setBillData({
            tableNumber, orders: tableOrders, subtotal, sgst, cgst,
            sgstAmount, cgstAmount, total,
            qrCodes: companyInfo?.qr_codes || []
        });
        setShowBillModal(true);
    };

    const updateOrderStatus = async (orderId, newStatus) => {
        try {
            // 1. Optimistically update IndexedDB so offline mode retains the new status
            try {
                const cachedOrders = await getAllFromStore('orders');
                const cOrder = cachedOrders.find(o => String(o.id) === String(orderId));
                if (cOrder) {
                    cOrder.order_status = newStatus;
                    await saveToStore('orders', cOrder);
                }
                const queue = await getAllFromStore('orders_queue');
                const qOrder = queue.find(o => String(o.id) === String(orderId) || String(o.order_id) === String(orderId));
                if (qOrder) {
                    qOrder.order_status = newStatus;
                    await saveToStore('orders_queue', qOrder);
                }
            } catch (dbErr) {
                console.warn('[IndexedDB] Failed to optimistic update status:', dbErr);
            }

            // 2. If it is an offline order that hasn't synced yet, don't hit the server!
            if (String(orderId).startsWith('offline_')) {
                return;
            }

            // 3. Update the server
            const response = await fetch(`${API_URL}/api/orders/${orderId}/status?_t=${Date.now()}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ order_status: newStatus, status: newStatus })
            });

            if (response.status === 401) {
                logout();
                return;
            }
            if (response.status === 403) {
                return;
            }

            const data = await response.json();
            if (data.success) {
                // Status updated successfully
            }
        } catch (error) {
            console.error('Error updating order status:', error);
            // It will fallback to the IndexedDB value which we already updated
        }
    };

    const handleDragStart = (e, order) => {
        setDraggedOrder(order);
        e.dataTransfer.effectAllowed = 'move';
        e.currentTarget.style.opacity = '0.4';
    };

    const handleDragEnd = (e) => {
        e.currentTarget.style.opacity = '1';
        setDraggedOrder(null);
    };

    const handleDragOver = (e) => {
        if (e.preventDefault) e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        return false;
    };

    const handleDrop = async (e, targetStatus) => {
        e.stopPropagation();
        e.preventDefault();
        if (draggedOrder && draggedOrder.order_status !== targetStatus) {
            // Optimistically update UI state for immediate visual feedback
            setOrders(prev => prev.map(o => String(o.id) === String(draggedOrder.id) ? { ...o, order_status: targetStatus } : o));
            
            await updateOrderStatus(draggedOrder.id, targetStatus);
            await loadOrders();
        }
        return false;
    };



    useEffect(() => {
        if (!token) return;

        const socket = io(API_URL, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 3000,
            reconnectionDelayMax: 15000,
            timeout: 10000,
            auth: { token }
        });

        socket.on('connect', () => {
            setConnectionStatus('🟢 Connected');
            console.log('[Connection KDS] Socket connected. Fetching latest orders to reconcile any missed events...');
            loadOrders();
        });

        socket.on('disconnect', () => {
            if (!navigator.onLine) {
                setConnectionStatus('🟡 Offline Mode');
            } else {
                setConnectionStatus('🔴 Disconnected');
            }
        });

        socket.on('connect_error', (err) => {
            // When the network is truly down, show Offline Mode — not a confusing server error
            if (!navigator.onLine) {
                setConnectionStatus('🟡 Offline Mode');
            } else {
                setConnectionStatus(`🔴 Error: ${err.message}`);
            }
            if (err.message === 'Authentication error' || err.message === 'jwt expired') {
                logout();
            }
        });

        const handleOrderSync = (updatedOrder) => {
            setOrders(prevOrders => {
                if (selectedBranch && updatedOrder.branch_id && String(updatedOrder.branch_id) !== String(selectedBranch)) {
                    return prevOrders.filter(o => String(o.id) !== String(updatedOrder.id));
                }
                const exists = prevOrders.some(o => String(o.id) === String(updatedOrder.id));
                let newOrders;
                if (exists) {
                    newOrders = prevOrders.map(o => String(o.id) === String(updatedOrder.id) ? { ...o, ...updatedOrder } : o);
                } else {
                    newOrders = [updatedOrder, ...prevOrders];
                }
                return newOrders.sort((a, b) => {
                    if (a.is_waiting !== b.is_waiting) return a.is_waiting ? 1 : -1;
                    return parseDateSafe(a.created_at) - parseDateSafe(b.created_at);
                });
            });
        };

        socket.on('new-order', (order) => {
            if (selectedBranch && order.branch_id && String(order.branch_id) !== String(selectedBranch)) return;
            setOrders(prevOrders => {
                if (prevOrders.some(o => String(o.id) === String(order.id))) {
                    return prevOrders;
                }
                const updatedOrders = [order, ...prevOrders];
                return updatedOrders.sort((a, b) => {
                    if (a.is_waiting !== b.is_waiting) return a.is_waiting ? 1 : -1;
                    return parseDateSafe(a.created_at) - parseDateSafe(b.created_at);
                });
            });
            playNotificationSound();

            const isOnline = isOnlineOrder(order);
            const message = order.is_waiting
                ? `New Waiting Order received! ⏳`
                : isOnline
                    ? `New Order received from ${order.payment_method.charAt(0).toUpperCase() + order.payment_method.slice(1)}! 🥡`
                    : `New Order received for Table ${order.table_number}! 🍽️`;

            showToast(message, isOnline ? 'warning' : 'success');

            if (hasNotificationSupport && notificationPermission === 'granted') {
                new Notification('New Order Received!', {
                    body: message
                });
            }
        });

        socket.on('order-updated', handleOrderSync);
        socket.on('order-status-updated', handleOrderSync);

        socket.on('waiting-list-updated', () => {
            loadOrders();
        });

        if (hasNotificationSupport && Notification.permission === 'default') {
            Notification.requestPermission().then(setNotificationPermission);
        }

        loadOrders();
        const intervalId = setInterval(loadOrders, 30000);

        const handleClickOutside = (event) => {
            if (!event.target.closest('.language-dropdown-container') && !event.target.closest('.branch-dropdown-container')) {
                setShowLanguageDropdown(false);
                setShowBranchDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            clearInterval(intervalId);
            socket.disconnect();
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [loadOrders, notificationPermission, token, logout, selectedBranch, API_URL, showToast]);

    // Broadcast Channel Listener for Offline Mode real-time sync (POS ⇄ KDS)
    useEffect(() => {
        try {
            const channel = new BroadcastChannel('redsorm_sync_channel');
            channel.onmessage = (event) => {
                const { type, data } = event.data || {};

                if (type === 'NEW_OFFLINE_ORDER' && data) {
                    // Filter by branch
                    if (selectedBranch && data.branch_id && String(data.branch_id) !== String(selectedBranch)) {
                        return;
                    }
                    console.log('[KDS Broadcast] Received new offline order:', data);
                    // Normalize items so item_name is always present for rendering
                    const normalizedOrder = {
                        ...data,
                        items: (data.items || []).map(item => ({
                            ...item,
                            item_name: item.item_name || item.name || '',
                            item_status: item.item_status || 'pending'
                        }))
                    };
                    normalizedOrder.order_status = data.order_status || 'pending';
                    setOrders(prev => {
                        if (prev.some(o => String(o.id) === String(normalizedOrder.id) || (o.order_id && normalizedOrder.order_id && String(o.order_id) === String(normalizedOrder.order_id)))) {
                            return prev;
                        }
                        const updated = [normalizedOrder, ...prev];
                        return updated.sort((a, b) => {
                            if (a.is_waiting !== b.is_waiting) {
                                return a.is_waiting ? 1 : -1;
                            }
                            return new Date(a.created_at) - new Date(b.created_at);
                        });
                    });
                    playNotificationSound();
                    showToast(`New offline order for Table ${normalizedOrder.table_number || 'N/A'}! 🍽️`, 'warning');
                }

                if (type === 'ORDER_STATUS_CHANGED' && data) {
                    console.log('[KDS Broadcast] Order status changed:', data);
                    setOrders(prev => prev.map(o => String(o.id) === String(data.orderId) ? { ...o, order_status: data.newStatus, updated_at: new Date().toISOString() } : o));
                }

                if (type === 'ORDERS_SYNCED') {
                    // Customer synced offline orders to server — reload from server
                    console.log('[KDS Broadcast] Orders synced to server, reloading...');
                    loadOrders();
                }
            };
            return () => channel.close();
        } catch (bcErr) {
            console.warn('[Broadcast Channel] Not supported or failed to initialize:', bcErr);
        }
    }, [showToast, selectedBranch, loadOrders]);

    // Offline Synchronization Manager for Kitchen Page (KDS ⇄ Cloud)
    const syncOfflineOrders = useCallback(async () => {
        try {
            const queue = await getQueue();
            if (!queue || queue.length === 0) return;

            console.log(`[Offline Sync KDS] Found ${queue.length} offline orders to sync...`);

            let syncedCount = 0;

            // Per-order try-catch: one failed POST no longer aborts the entire loop
            for (const order of queue) {
                try {
                    const apiOrderData = {
                        table_number: order.table_number,
                        is_waiting: order.is_waiting,
                        items: (order.items || []).map(item => ({
                            id: item.menu_item_id || item.id,
                            quantity: item.quantity,
                            price_inr: item.price_inr,
                            price_usd: item.price_usd,
                            name: item.item_name || item.name,
                            discount_percentage: item.discount_percentage || 0
                        })),
                        currency: order.currency,
                        payment_method: order.payment_method,
                        customer_id: order.customer_id,
                        customer_name: order.customer_name,
                        branch_id: order.branch_id,
                        session_id: order.session_id,
                        notes: order.notes,
                        order_status: order.order_status
                    };

                    const response = await fetch(`${API_URL}/api/orders`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(apiOrderData)
                    });

                    const data = await response.json();
                    if (data.success) {
                        console.log(`[Offline Sync KDS] Synchronized order ${order.id} successfully!`);
                        await removeFromQueue(order.id);
                        setOrders(prev => prev.filter(o => String(o.id) !== String(order.id)));
                        syncedCount++;
                    } else {
                        console.error(`[Offline Sync KDS] Failed to sync order ${order.id}:`, data.message);
                    }
                } catch (orderErr) {
                    // Network error for this specific order — leave in queue for next retry
                    console.warn(`[Offline Sync KDS] Could not sync order ${order.id} (will retry):`, orderErr.message);
                }
            }

            // Only reload and notify if at least one order was actually synced
            if (syncedCount > 0) {
                console.log(`[Offline Sync KDS] ${syncedCount} order(s) synced to server. Reloading KDS...`);
                loadOrders();
                showToast(`${syncedCount} offline order(s) synchronized successfully!`, 'success');
                // Notify other open tabs (e.g., Admin) to refresh their order list
                try {
                    const bc = new BroadcastChannel('redsorm_sync_channel');
                    bc.postMessage({ type: 'ORDERS_SYNCED' });
                    setTimeout(() => { try { bc.close(); } catch (e) {} }, 1000);
                } catch (bcErr) {
                    console.warn('[KDS Sync] BroadcastChannel notify failed:', bcErr);
                }
            }
        } catch (error) {
            // Top-level error (e.g., getQueue failed) — log and silently exit
            console.error('[Offline Sync KDS] Unexpected error during synchronization:', error);
        }
    }, [token, loadOrders, showToast, API_URL]);

    useEffect(() => {
        if (!token) return;

        const handleOnline = () => {
            console.log('[Connection KDS] Network online. Syncing offline orders and reloading...');
            syncOfflineOrders();
            loadOrders(); // Always force a reload from server to catch orders synced by other devices
        };

        window.addEventListener('online', handleOnline);

        if (navigator.onLine) {
            syncOfflineOrders();
        }

        const syncInterval = setInterval(() => {
            if (navigator.onLine) {
                syncOfflineOrders();
            }
        }, 30000);

        return () => {
            window.removeEventListener('online', handleOnline);
            clearInterval(syncInterval);
        };
    }, [token, syncOfflineOrders, loadOrders]);

    // Offline Queue Poller: When the network is down, periodically read the IndexedDB
    // orders_queue and merge any pending offline orders into the KDS display.
    // This handles the BroadcastChannel race condition (message sent before listener ready)
    // and ensures orders placed on the same device always appear in the KDS.
    // Use a ref to always access the latest selectedBranch without stale closure.
    const selectedBranchRef = useRef(selectedBranch);
    useEffect(() => {
        selectedBranchRef.current = selectedBranch;
    }, [selectedBranch]);

    useEffect(() => {
        let pollInterval = null;

        const pollOfflineQueue = async () => {
            // Always poll the queue — navigator.onLine can be true even when
            // the backend server is unreachable (xhr poll error scenario)
            try {
                const queuedOrders = await getQueue();
                if (!queuedOrders || queuedOrders.length === 0) return;

                // Read latest selectedBranch from ref to avoid stale closure
                const currentBranch = selectedBranchRef.current;

                setOrders(prev => {
                    let changed = false;
                    let updated = [...prev];

                    queuedOrders.forEach(qOrder => {
                        // Skip if already shown
                        if (updated.some(o => String(o.id) === String(qOrder.id) || (o.order_id && qOrder.order_id && String(o.order_id) === String(qOrder.order_id)))) return;

                        // Branch filter using ref value (always current)
                        if (currentBranch && qOrder.branch_id && String(qOrder.branch_id) !== String(currentBranch)) return;

                        // Normalize items for rendering
                        const normalizedOrder = {
                            ...qOrder,
                            order_status: qOrder.order_status || 'pending',
                            items: (qOrder.items || []).map(item => ({
                                ...item,
                                item_name: item.item_name || item.name || '',
                                item_status: item.item_status || 'pending'
                            }))
                        };

                        updated = [normalizedOrder, ...updated];
                        changed = true;
                        console.log('[KDS Offline Poll] Added queued order to display:', normalizedOrder.id);
                    });

                    if (!changed) return prev;

                    return updated.sort((a, b) => {
                        if (a.is_waiting !== b.is_waiting) return a.is_waiting ? 1 : -1;
                        return new Date(a.created_at) - new Date(b.created_at);
                    });
                });
            } catch (err) {
                console.warn('[KDS Offline Poll] Error reading offline queue:', err);
            }
        };

        // Start polling every 2 seconds — faster detection is critical for offline UX
        pollInterval = setInterval(pollOfflineQueue, 2000);
        // Also run immediately on mount
        pollOfflineQueue();

        const handleOffline = () => {
            // Network went offline — update status and show queued orders immediately
            setConnectionStatus('🟡 Offline Mode');
            pollOfflineQueue();
        };

        const handleOnlineStatus = () => {
            setConnectionStatus('🟢 Connected');
        };

        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnlineStatus);

        return () => {
            clearInterval(pollInterval);
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnlineStatus);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const getOrdersByStatus = (status) => {
        const isNoTables = companyInfo?.no_tables_mode || currentUser?.company_id === 36;
        let filtered = orders.filter(o => {
            if (status === 'delivered') {
                return o.order_status === 'delivered' || o.order_status === 'completed';
            }
            if (status === 'preparing' && isNoTables) {
                return o.order_status === 'preparing' || o.order_status === 'pending';
            }
            return o.order_status === status;
        });

        if (showOnlineOrders) {
            filtered = filtered.filter(o => {
                const method = (o.payment_method || '').toLowerCase();
                return method === 'swiggy' || method === 'zomato';
            });
        } else {
            filtered = filtered.filter(o => {
                const method = (o.payment_method || '').toLowerCase();
                return method !== 'swiggy' && method !== 'zomato';
            });
        }

        if (currentUser?.company_id === 36) {
            filtered = filtered.filter(o => o.payment_status === 'paid' || o.payment_status === 'completed');
        }

        if (status === 'delivered') {
            const { start, end } = getDateRange(deliveredDateFilter);
            filtered = filtered.filter(order => {
                const orderDate = parseDateSafe(order.created_at);
                return orderDate >= start && orderDate < end;
            });
        }
        return filtered;
    };

    const statusCounts = {
        pending: getOrdersByStatus('pending').length,
        preparing: getOrdersByStatus('preparing').length,
        ready: getOrdersByStatus('ready').length,
        delivered: getOrdersByStatus('delivered').length
    };

    const renderOrderCard = (order) => {
        const { mins, secs, totalMins } = getElapsedTimeParts(order.created_at);
        const isLate = totalMins > 15;

        return (
            <div
                key={order.id}
                draggable={order.order_status !== 'delivered' && order.order_status !== 'completed'}
                onDragStart={(e) => handleDragStart(e, order)}
                onDragEnd={handleDragEnd}
                className={`bg-white rounded-xl border-2 ${isLate ? 'border-red-400' : 'border-emerald-400'} p-4 ${(order.order_status !== 'delivered' && order.order_status !== 'completed') ? 'cursor-move' : ''}`}
            >
                {/* Top Section - Compact Info */}
                <div className="flex items-start justify-between mb-4 pb-3 border-b">
                    <div className="flex items-center gap-3">
                        {/* Table Number / Waiting Number / Online Platform */}
                        <div className={`min-w-[56px] h-14 px-2.5 rounded-lg ${isOnlineOrder(order) ? (order.payment_method.toLowerCase() === 'swiggy' ? 'bg-amber-500' : 'bg-rose-600') : order.is_waiting ? 'bg-orange-600' : isLate ? 'bg-red-500' : 'bg-emerald-600'} text-white flex flex-col items-center justify-center leading-none`}>
                            {isOnlineOrder(order) ? (
                                <>
                                    <i className="fas fa-shopping-bag text-sm mb-0.5"></i>
                                    <span className="text-[9px] font-black uppercase tracking-tighter">{order.payment_method}</span>
                                </>
                            ) : order.is_waiting ? (
                                <>
                                    <span className="text-xs font-bold">Wait</span>
                                    <span className="text-xl font-black">#{order.waiting_number}</span>
                                </>
                            ) : (order.table_number === 0 || order.table_number === '0') ? (
                                parseInt(companyInfo?.id || currentUser?.company_id) === 37 ? (
                                    <span className="text-sm font-black whitespace-nowrap">Delivery</span>
                                ) : (
                                    <>
                                        <span className="text-[9px] font-black uppercase tracking-tighter">Order</span>
                                        <span className="text-lg font-black">#{order.order_number || order.id}</span>
                                    </>
                                )
                            ) : (
                                <span className="text-sm font-black whitespace-nowrap">Table - {order.table_number}</span>
                            )}
                        </div>

                        {/* Order Info */}
                        <div>
                            {order.customer_name && (
                                <div className="text-base font-bold text-gray-900">{order.customer_name}</div>
                            )}
                            <div className="text-sm font-semibold text-gray-400">{parseDateSafe(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                            {currentUser?.company_id === 36 && order.payment_notes && (
                                <div className="text-xs font-bold text-blue-600 mt-1 bg-blue-50 px-2 py-0.5 rounded border border-blue-200 inline-block">
                                    {order.payment_notes}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Timer */}
                    {order.order_status !== 'delivered' && order.order_status !== 'completed' && (
                        order.order_status === 'ready' ? (
                            <div className="text-right">
                                <div className="inline-flex flex-col items-center justify-center bg-teal-50 border-2 border-teal-400 rounded-xl px-3 py-2">
                                    <i className="fas fa-check-circle text-teal-500 text-lg mb-0.5"></i>
                                    <span className="text-xs font-black text-teal-600 tracking-tight">READY</span>
                                    <span className="text-[10px] font-bold text-teal-500 opacity-70">{mins.toString().padStart(2,'0')}m taken</span>
                                </div>
                            </div>
                        ) : (
                            <div className={`text-right ${isLate ? 'text-red-600' : 'text-emerald-600'}`}>
                                <div className="text-sm font-bold tracking-normal opacity-70">Timer</div>
                                <div className="text-3xl font-black font-mono tracking-tight">
                                    {mins.toString().padStart(2, '0')}:{secs.toString().padStart(2, '0')}
                                </div>
                            </div>
                        )
                    )}
                </div>

                {/* Items List - Simple and Clean */}
                <div className="space-y-1 mb-3">
                    {order.items.map((item, idx) => (
                        <div
                            key={idx}
                            className={`flex items-center gap-3 py-1 ${item.item_status === 'cancelled' ? 'opacity-40' : ''}`}
                        >
                            <div className={`w-11 h-11 rounded-lg ${item.item_status === 'cancelled' ? 'bg-gray-200 text-gray-500' : 'bg-emerald-50 text-emerald-700 border border-emerald-100'} flex items-center justify-center text-xl font-black shadow-sm`}>
                                {item.quantity}×
                            </div>
                            <div className="flex-1">
                                <div className={`text-lg font-bold ${item.item_status === 'cancelled' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                    {item.item_name}
                                </div>
                                {item.item_status === 'cancelled' && (
                                    <div className="text-sm text-red-500 font-bold">Cancelled</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Notes */}
                {order.notes && (
                    <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="text-sm font-bold text-amber-700 mb-1">Note:</div>
                        <div className="text-base font-semibold text-amber-900">{order.notes}</div>
                    </div>
                )}

                {/* Drag Hint */}
                {order.order_status !== 'delivered' && order.order_status !== 'completed' && (
                    <div className="text-center py-2.5 text-sm font-medium text-gray-500 border-t">
                        <i className="fas fa-grip-vertical mr-2"></i>
                        Drag to Change Status
                    </div>
                )}
            </div>
        );
    };

    const hasKitchenPermission = (() => {
        if (authLoading) return true;
        if (!currentUser) return false;
        const roleStr = (currentUser.role || '').toLowerCase();
        if (roleStr === 'admin' || roleStr === 'owner' || roleStr === 'superadmin') return true;

        let perms = currentUser.permissions;
        if (typeof perms === 'string') {
            try { perms = JSON.parse(perms); } catch (e) { perms = {}; }
        }
        if (!perms) return false;
        return !!(perms.kitchen === true);
    })();

    if (!authLoading && !hasKitchenPermission) {
        return (
            <div className="digital-emerald-theme min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center border border-gray-100">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="fas fa-lock text-2xl text-red-600"></i>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('unauthorized')}</h1>
                    <p className="text-gray-600 mb-6">{t('noPermissionKitchen') || "You do not have permission to access the Kitchen Display."}</p>
                    <button onClick={() => navigate('/homepage')} className="w-full btn-add-highlight py-2.5 rounded-xl font-bold">
                        {t('goBackToHome') || "Go Back to Home"}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="digital-emerald-theme min-h-screen bg-gray-50" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&display=swap');
                `}
            </style>
            <audio ref={audioRef} src="/notification.mp3" />

            {/* Header */}
            <div className="bg-white border-b sticky top-0 z-50 expand-in">
                <div className="max-w-[1920px] mx-auto px-3 sm:px-6 py-3 sm:py-4">
                    {/* Mobile: Two rows - Title on top, Controls below */}
                    <div className="block sm:hidden">
                        {/* Row 1: Title */}
                        <div className="flex items-center gap-3 mb-3">
                            {companyInfo?.logo_url && (
                                <img src={companyInfo.logo_url} alt="Logo" className="h-8 w-8 object-contain flex-shrink-0" />
                            )}
                            <h1 className="text-lg font-bold truncate">{t('kitchenDisplay')}</h1>
                        </div>
                        
                        {/* Row 2: Connected + Controls */}
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
                                <div className={`w-1.5 h-1.5 rounded-full ${connectionStatus.includes('🟢') ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`}></div>
                                <span className="text-xs font-bold text-emerald-700 tracking-normal">{connectionStatus.replace('🟢 ', '').replace('🔴 ', '')}</span>
                            </div>
                            
                            <div className="flex items-center gap-2">
                                {!currentUser?.branch_id && (
                                    <div className="relative branch-dropdown-container">
                                        <button
                                            onClick={() => setShowBranchDropdown(!showBranchDropdown)}
                                            className="px-2 py-1.5 border rounded-lg text-xs flex items-center gap-1.5"
                                        >
                                            <i className="fas fa-store text-xs"></i>
                                            <i className="fas fa-chevron-down text-[10px]"></i>
                                        </button>
                                        {showBranchDropdown && (
                                            <div className="absolute top-full mt-1 right-0 w-48 bg-white border rounded-lg shadow-lg py-1 z-50">
                                                {branches && branches.length > 1 && (
                                                <button onClick={() => { setSelectedBranch(null); setShowBranchDropdown(false); }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50">All Branches</button>
                                                )}
                                                {branches.map(b => (
                                                    <button key={b.id} onClick={() => { setSelectedBranch(b.id); setShowBranchDropdown(false); }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50">{b.name}</button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="relative language-dropdown-container">
                                    <button onClick={() => setShowLanguageDropdown(!showLanguageDropdown)} className="w-9 h-9 border rounded-lg flex items-center justify-center">
                                        <i className="fas fa-globe text-sm"></i>
                                    </button>
                                    {showLanguageDropdown && (
                                        <div className="absolute top-full mt-1 right-0 w-48 bg-white border rounded-lg shadow-lg py-1 z-50 max-h-60 overflow-auto">
                                            <div className="px-3 py-2 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                                                <span className="text-xs font-bold text-gray-500 tracking-normal">Language</span>
                                                <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold tracking-normal">Soon</span>
                                            </div>
                                            {SUPPORTED_LANGUAGES.map(lang => (
                                                <button key={lang.code} onClick={() => { 
                                                    if (language === lang.code) {
                                                        setShowLanguageDropdown(false); 
                                                    } else {
                                                        alert('This language is coming soon!');
                                                    }
                                                }} className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between ${language === lang.code ? 'font-bold text-emerald-600 bg-emerald-50 cursor-pointer' : 'text-gray-400 opacity-70 cursor-not-allowed hover:bg-gray-50'}`}>
                                                    <span>{lang.label}</span>
                                                    {language === lang.code && <i className="fas fa-check text-emerald-600 text-[10px]"></i>}
                                                    {language !== lang.code && <i className="fas fa-clock text-amber-500 text-[10px]"></i>}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <button onClick={loadOrders} className="w-9 h-9 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-lg shadow-emerald-200 transition-all active:scale-95">
                                    <i className="fas fa-sync-alt text-sm"></i>
                                </button>
                            </div>
                        </div>
                        
                        {/* Row 3: Status Counts */}
                        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                            {[
                                { label: 'pending', count: statusCounts.pending, color: 'orange', bgColor: '#fff7ed', borderColor: '#fed7aa', textColor: '#c2410c' },
                                { label: 'preparing', count: statusCounts.preparing, color: 'emerald', bgColor: '#d1fae5', borderColor: '#a7f3d0', textColor: '#047857' },
                                { label: 'ready', count: statusCounts.ready, color: 'teal', bgColor: '#ccfbf1', borderColor: '#99f6e4', textColor: '#0d9488' },
                                { label: 'delivered', count: statusCounts.delivered, color: 'gray', bgColor: '#f3f4f6', borderColor: '#e5e7eb', textColor: '#374151' }
                            ].filter(s => {
                                if (companyInfo?.no_tables_mode || currentUser?.company_id === 36) {
                                    return s.label === 'preparing' || s.label === 'ready';
                                }
                                return true;
                            }).map((s, idx) => (
                                <div key={idx} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border-2 shadow-sm"
                                    style={{
                                        backgroundColor: s.bgColor,
                                        borderColor: s.borderColor
                                    }}>
                                    <span className="text-xl font-bold leading-none" style={{ color: s.textColor }}>{s.count}</span>
                                    <span className="text-sm font-bold tracking-normal" style={{ color: s.textColor }}>{t(s.label)}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Desktop: Single row layout */}
                    <div className="hidden sm:flex items-center justify-between">
                        {/* Left */}
                        <div className="flex items-center gap-4">
                            {companyInfo?.logo_url && (
                                <img src={companyInfo.logo_url} alt="Logo" className="h-10 w-10 object-contain flex-shrink-0" />
                            )}
                            <div>
                                <h1 className="text-xl font-bold">{t('kitchenDisplay')}</h1>
                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                    <div className="flex items-center gap-1.5 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                                        <div className={`w-1.5 h-1.5 rounded-full ${connectionStatus.includes('🟢') ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`}></div>
                                        <span className="text-xs font-bold text-emerald-700 tracking-normal">{connectionStatus.replace('🟢 ', '').replace('🔴 ', '')}</span>
                                    </div>
                                    <div className="h-4 w-px bg-gray-200 mx-1"></div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {[
                                            { label: 'pending', count: statusCounts.pending, color: 'orange', bgColor: '#fff7ed', borderColor: '#ffedd5', textColor: '#c2410c' },
                                            { label: 'preparing', count: statusCounts.preparing, color: 'emerald', bgColor: '#ecfdf5', borderColor: '#d1fae5', textColor: '#047857' },
                                            { label: 'ready', count: statusCounts.ready, color: 'teal', bgColor: '#f0fdfa', borderColor: '#ccfbf1', textColor: '#0d9488' },
                                            { label: 'delivered', count: statusCounts.delivered, color: 'gray', bgColor: '#f9fafb', borderColor: '#f3f4f6', textColor: '#374151' }
                                        ].filter(s => {
                                            if (companyInfo?.no_tables_mode || currentUser?.company_id === 36) {
                                                return s.label === 'preparing' || s.label === 'ready';
                                            }
                                            return true;
                                        }).map((s, idx) => (
                                            <div key={idx} className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg border shadow-sm transition-all hover:scale-105"
                                                style={{
                                                    backgroundColor: s.bgColor,
                                                    borderColor: s.borderColor
                                                }}>
                                                <span className="text-base font-bold leading-none" style={{ color: s.textColor }}>{s.count}</span>
                                                <span className="text-sm font-bold tracking-normal whitespace-nowrap" style={{ color: s.textColor }}>{t(s.label)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right */}
                        <div className="flex items-center gap-3">
                            {!currentUser?.branch_id && (
                                <div className="relative branch-dropdown-container">
                                    <button
                                        onClick={() => setShowBranchDropdown(!showBranchDropdown)}
                                        className="px-3 py-2 border rounded-lg text-sm flex items-center gap-2"
                                    >
                                        <i className="fas fa-store"></i>
                                        {selectedBranch ? branches.find(b => b.id === selectedBranch)?.name : 'All'}
                                        <i className="fas fa-chevron-down text-xs"></i>
                                    </button>
                                    {showBranchDropdown && (
                                        <div className="absolute top-full mt-1 right-0 w-48 bg-white border rounded-lg shadow-lg py-1 z-50">
                                            {branches && branches.length > 1 && (
                                            <button onClick={() => { setSelectedBranch(null); setShowBranchDropdown(false); }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50">All Branches</button>
                                            )}
                                            {branches.map(b => (
                                                <button key={b.id} onClick={() => { setSelectedBranch(b.id); setShowBranchDropdown(false); }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50">{b.name}</button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="relative language-dropdown-container">
                                <button onClick={() => setShowLanguageDropdown(!showLanguageDropdown)} className="w-10 h-10 border rounded-lg flex items-center justify-center">
                                    <i className="fas fa-globe"></i>
                                </button>
                                {showLanguageDropdown && (
                                    <div className="absolute top-full mt-1 right-0 w-48 bg-white border rounded-lg shadow-lg py-1 z-50 max-h-60 overflow-auto">
                                        <div className="px-3 py-2 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                                            <span className="text-xs font-bold text-gray-500 tracking-normal">Language</span>
                                            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold tracking-normal">Soon</span>
                                        </div>
                                        {SUPPORTED_LANGUAGES.map(lang => (
                                            <button key={lang.code} onClick={() => { 
                                                if (language === lang.code) {
                                                    setShowLanguageDropdown(false); 
                                                } else {
                                                    alert('This language is coming soon!');
                                                }
                                            }} className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between ${language === lang.code ? 'font-bold text-emerald-600 bg-emerald-50 cursor-pointer' : 'text-gray-400 opacity-70 cursor-not-allowed hover:bg-gray-50'}`}>
                                                <span>{lang.label}</span>
                                                {language === lang.code && <i className="fas fa-check text-emerald-600 text-[10px]"></i>}
                                                {language !== lang.code && <i className="fas fa-clock text-amber-500 text-[10px]"></i>}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <button onClick={loadOrders} className="w-10 h-10 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-lg shadow-emerald-200 transition-all active:scale-95">
                                <i className="fas fa-sync-alt"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <main className="max-w-[1920px] mx-auto px-3 sm:px-6 py-4 sm:py-6">
                {/* Date Filter for Delivered / Online Orders Toggle */}
                <div className="flex justify-between items-center mb-4 expand-in flex-wrap gap-3" style={{ animationDelay: '0.1s' }}>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            onClick={() => setShowOnlineOrders(!showOnlineOrders)}
                            className={`relative px-4 py-2 rounded-xl text-xs font-bold tracking-normal transition-all whitespace-nowrap shadow-sm border flex items-center gap-2 ${
                                showOnlineOrders 
                                    ? 'bg-amber-500 text-white border-amber-500 shadow-md shadow-amber-200' 
                                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                            }`}
                        >
                            <i className="fas fa-shopping-bag"></i>
                            {showOnlineOrders ? (
                                <span className="flex items-center gap-1.5">
                                    Show Dine-in
                                </span>
                            ) : (
                                <span className="flex items-center gap-1.5">
                                    Online Orders
                                </span>
                            )}
                            {/* Animated badge for pending online count when NOT in online view */}
                            {!showOnlineOrders && pendingOnlineCount > 0 && (
                                <span className="absolute -top-2 -right-2 flex items-center justify-center">
                                    <span className="absolute inline-flex w-5 h-5 rounded-full bg-red-400 opacity-75 animate-ping"></span>
                                    <span className="relative inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-black shadow-md">
                                        {pendingOnlineCount}
                                    </span>
                                </span>
                            )}
                            {/* Animated badge for pending dine-in count when in online view */}
                            {showOnlineOrders && pendingDineInCount > 0 && (
                                <span className="absolute -top-2 -right-2 flex items-center justify-center">
                                    <span className="absolute inline-flex w-5 h-5 rounded-full bg-red-400 opacity-75 animate-ping"></span>
                                    <span className="relative inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-black shadow-md">
                                        {pendingDineInCount}
                                    </span>
                                </span>
                            )}
                        </button>

                        {showOnlineOrders && (
                            <button
                                onClick={openAddOnlineModal}
                                className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-xs font-black rounded-xl shadow-md shadow-amber-200 transition-all active:scale-95 flex items-center gap-2"
                            >
                                <i className="fas fa-plus-circle"></i>
                                Add Online Order
                            </button>
                        )}
                    </div>

                    <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-white rounded-xl border border-gray-100 px-2.5 sm:px-2 py-1.5 shadow-sm w-full sm:w-auto overflow-x-auto">
                        <span className="text-xs sm:text-xs font-bold text-gray-600 tracking-normal flex-shrink-0">Delivered:</span>
                        <div className="flex gap-1.5 sm:gap-2">
                            {['today', 'yesterday', 'week', 'month'].map(filter => (
                                <button
                                    key={filter}
                                    onClick={() => setDeliveredDateFilter(filter)}
                                    className={`px-3 sm:px-4 py-1.5 rounded-lg text-xs font-bold tracking-normal transition-all whitespace-nowrap flex-shrink-0 ${deliveredDateFilter === filter ? 'bg-emerald-600 text-white shadow-md shadow-emerald-200' : 'text-gray-500 hover:bg-emerald-50'}`}
                                >
                                    {t(filter)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Side-by-Side Kanban Board with Horizontal Scroll */}
                <div className="flex flex-nowrap items-stretch overflow-x-auto gap-6 pb-8 no-scrollbar scroll-smooth expand-in" style={{ WebkitOverflowScrolling: 'touch', animationDelay: '0.2s' }}>
                    {[
                        { status: 'pending', color: 'orange' },
                        { status: 'preparing', color: 'emerald' },
                        { status: 'ready', color: 'teal' },
                        { status: 'delivered', color: 'gray' }
                    ].filter(col => {
                        if (companyInfo?.no_tables_mode || currentUser?.company_id === 36) {
                            return col.status === 'preparing' || col.status === 'ready';
                        }
                        return true;
                    }).map(({ status, color }) => (
                        <div
                            key={status}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, status)}
                            className="flex-shrink-0 w-[300px] md:w-[350px] lg:w-[400px] flex flex-col h-full"
                        >
                            {/* Column Header */}
                            <div className={`px-3 sm:px-4 py-2.5 sm:py-3 text-white rounded-t-2xl mb-1 flex items-center justify-between shadow-lg`}
                                style={{
                                    backgroundColor: color === 'orange' ? '#ea580c' :
                                        color === 'emerald' ? '#059669' :
                                            color === 'teal' ? '#0d9488' : '#6b7280'
                                }}
                            >
                                <span className="font-bold text-xs sm:text-sm tracking-normal">{t(status)}</span>
                                <span className="bg-white/20 px-2 sm:px-2.5 py-1 rounded-lg text-xs sm:text-sm font-bold shadow-inner">{statusCounts[status]}</span>
                            </div>

                            {/* Drop Zone */}
                            <div className="flex-1 bg-gray-100/50 rounded-b-2xl p-3 space-y-4 min-h-[70vh] border-x border-b border-gray-200/50 backdrop-blur-sm shadow-inner">
                                {getOrdersByStatus(status).length === 0 ? (
                                    <div className="text-center py-32 text-red-600 font-normal">
                                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm border border-red-100">
                                            <i className="fas fa-clipboard-list text-xl text-red-200"></i>
                                        </div>
                                        <div className="text-sm sm:text-base font-bold tracking-normal">{t('noOrders')}</div>
                                    </div>
                                ) : (
                                    getOrdersByStatus(status).map(order => renderOrderCard(order))
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </main>

            {/* Bill Modal */}
            {showBillModal && billData && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm" onClick={() => setShowBillModal(false)}>
                    <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl scale-100 transition-all" onClick={e => e.stopPropagation()}>
                        <div className="p-8 bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h2 className="text-3xl font-bold tracking-tight">{t('bill')}</h2>
                                    <p className="text-emerald-50 text-xs font-bold tracking-normal mt-1 opacity-80">{(billData.tableNumber === 0 || billData.tableNumber === '0') ? (parseInt(companyInfo?.id || currentUser?.company_id) === 37 ? 'Delivery' : `Order #${billData.orders?.[0]?.order_number || billData.orders?.[0]?.id || ''}`) : `Table ${billData.tableNumber}`}</p>
                                    {((parseInt(companyInfo?.id) === 38 || parseInt(currentUser?.company_id) === 38) && billData.orders) && (() => {
                                        const orderNums = [...new Set(billData.orders.map(o => o.order_number).filter(Boolean))].sort((a, b) => a - b);
                                        if (orderNums.length > 0) {
                                            return <p className="text-emerald-100 text-sm font-black mt-1">Order Number: {orderNums.join(', ')}</p>;
                                        }
                                        return null;
                                    })()}
                                </div>
                                <button onClick={() => setShowBillModal(false)} className="w-10 h-10 bg-white/20 hover:bg-white/40 rounded-full transition-all flex items-center justify-center shadow-inner">
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>

                        <div className="p-8 space-y-6 max-h-[60vh] overflow-y-auto">
                            {billData.orders.map(order => (
                                <div key={order.id} className="space-y-3 pb-4 border-b border-gray-50 last:border-0 last:pb-0">
                                    <p className="text-[10px] font-bold text-gray-400 tracking-normal">Order #{order.id.toString().slice(-6)}</p>
                                    {order.items.map((item, idx) => (
                                        <div key={idx} className="flex justify-between text-sm items-center">
                                            <div className="flex items-center gap-3">
                                                <span className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-[11px] font-black text-gray-800">{item.quantity}×</span>
                                                <span className="font-bold text-gray-700 text-xs">{item.item_name}</span>
                                            </div>
                                            <span className="font-black text-gray-900">{formatAmount(parseFloat(item.price || item.price_inr) * item.quantity)}</span>
                                        </div>
                                    ))}
                                </div>
                            ))}

                            <div className="space-y-3 pt-6 border-t border-gray-100">
                                <div className="flex justify-between text-[11px] font-bold text-gray-400 tracking-normal">
                                    <span>Subtotal</span>
                                    <span className="text-gray-600">{formatAmount(billData.subtotal)}</span>
                                </div>
                                <div className="flex justify-between text-[11px] font-bold text-gray-400 tracking-normal">
                                    <span>GST ({billData.sgst + billData.cgst}%)</span>
                                    <span className="text-gray-600">{formatAmount(billData.sgstAmount + billData.cgstAmount)}</span>
                                </div>
                                <div className="flex justify-between text-2xl font-bold pt-4 border-t border-gray-100">
                                    <span className="tracking-tight">Total</span>
                                    <span className="text-emerald-600 tracking-tight">{formatAmount(billData.total)}</span>
                                </div>
                            </div>

                            {billData.qrCodes && billData.qrCodes.length > 0 && (
                                <div className="p-4 bg-gray-50 rounded-lg text-center">
                                    <p className="text-xs text-gray-500 mb-3">Scan to Pay</p>
                                    <img src={Array.isArray(billData.qrCodes) ? billData.qrCodes[0] : ''} alt="QR" className="w-32 h-32 mx-auto" />
                                </div>
                            )}
                        </div>

                        <div className="p-6 pt-0">
                            <button onClick={() => window.print()} className="w-full h-12 bg-gray-900 text-white rounded-lg font-semibold">
                                <i className="fas fa-print mr-2"></i> {t('printBill')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Manual Online Order Entry Modal */}
            {showAddOnlineModal && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-gray-900/70 backdrop-blur-sm" onClick={() => setShowAddOnlineModal(false)}>
                    <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 flex items-center justify-between flex-shrink-0">
                            <div>
                                <h2 className="text-white font-black text-lg tracking-tight">Add Online Order</h2>
                                <p className="text-amber-100 text-xs font-bold mt-0.5">Enter the order details from your Swiggy / Zomato app</p>
                            </div>
                            <button onClick={() => setShowAddOnlineModal(false)} className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-white transition-all">
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="overflow-y-auto flex-1 p-5 space-y-4">
                            {/* Platform */}
                            <div>
                                <label className="text-xs font-black text-gray-500 uppercase tracking-widest mb-2 block">Platform</label>
                                <div className="flex gap-3">
                                    {['swiggy', 'zomato'].map(p => (
                                        <button
                                            key={p}
                                            onClick={() => setAddOnlineForm(prev => ({ ...prev, platform: p }))}
                                            className={`flex-1 py-3 rounded-xl font-black text-sm transition-all border-2 flex items-center justify-center gap-2 ${
                                                addOnlineForm.platform === p
                                                    ? p === 'swiggy'
                                                        ? 'bg-orange-500 text-white border-orange-500 shadow-lg shadow-orange-200'
                                                        : 'bg-red-600 text-white border-red-600 shadow-lg shadow-red-200'
                                                    : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'
                                            }`}
                                        >
                                            <i className="fas fa-shopping-bag"></i>
                                            {p.charAt(0).toUpperCase() + p.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Customer / Order Ref */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-black text-gray-500 uppercase tracking-widest mb-1.5 block">Customer Name</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Rahul"
                                        value={addOnlineForm.customerName}
                                        onChange={e => setAddOnlineForm(prev => ({ ...prev, customerName: e.target.value }))}
                                        className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm font-semibold focus:border-amber-400 focus:outline-none transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-black text-gray-500 uppercase tracking-widest mb-1.5 block">Order Ref #</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. SW123456"
                                        value={addOnlineForm.orderRef}
                                        onChange={e => setAddOnlineForm(prev => ({ ...prev, orderRef: e.target.value }))}
                                        className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm font-semibold focus:border-amber-400 focus:outline-none transition-colors"
                                    />
                                </div>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="text-xs font-black text-gray-500 uppercase tracking-widest mb-1.5 block">Special Instructions (optional)</label>
                                <input
                                    type="text"
                                    placeholder="e.g. No onions, extra spicy..."
                                    value={addOnlineForm.notes}
                                    onChange={e => setAddOnlineForm(prev => ({ ...prev, notes: e.target.value }))}
                                    className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm font-semibold focus:border-amber-400 focus:outline-none transition-colors"
                                />
                            </div>

                            {/* Items */}
                            <div>
                                <label className="text-xs font-black text-gray-500 uppercase tracking-widest mb-2 block">Select Items *</label>
                                {menuLoadingForModal ? (
                                    <div className="text-center py-8 text-gray-400">
                                        <i className="fas fa-spinner fa-spin text-2xl mb-2"></i>
                                        <div className="text-sm font-bold">Loading menu...</div>
                                    </div>
                                ) : menuItemsList.length === 0 ? (
                                    <div className="text-center py-6 text-gray-400 bg-gray-50 rounded-xl">
                                        <i className="fas fa-utensils text-2xl mb-2"></i>
                                        <div className="text-sm font-bold">No menu items found</div>
                                    </div>
                                ) : (
                                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                                        {menuItemsList.map(item => {
                                            const selected = addOnlineForm.items.find(i => i.id === item.id);
                                            return (
                                                <div
                                                    key={item.id}
                                                    className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                                                        selected ? 'border-amber-400 bg-amber-50' : 'border-gray-100 bg-gray-50 hover:border-gray-200'
                                                    }`}
                                                    onClick={() => toggleModalItem(item)}
                                                >
                                                    <div className={`w-5 h-5 rounded-md flex items-center justify-center border-2 flex-shrink-0 transition-all ${
                                                        selected ? 'bg-amber-500 border-amber-500' : 'border-gray-300'
                                                    }`}>
                                                        {selected && <i className="fas fa-check text-white text-[10px]"></i>}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-bold text-sm text-gray-900 truncate">{item.name}</div>
                                                        {item.category && <div className="text-xs text-gray-400 font-semibold truncate">{item.category}</div>}
                                                    </div>
                                                    <div className="text-sm font-black text-gray-700 flex-shrink-0">₹{item.price_inr}</div>
                                                    {selected && (
                                                        <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                                            <button onClick={() => changeModalItemQty(item.id, -1)} className="w-7 h-7 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 font-black text-sm flex items-center justify-center">
                                                                <i className="fas fa-minus text-xs"></i>
                                                            </button>
                                                            <span className="w-6 text-center font-black text-sm text-gray-900">{selected.qty}</span>
                                                            <button onClick={() => changeModalItemQty(item.id, 1)} className="w-7 h-7 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 font-black text-sm flex items-center justify-center">
                                                                <i className="fas fa-plus text-xs"></i>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Selected Summary */}
                            {addOnlineForm.items.length > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                                    <div className="text-xs font-black text-amber-800 mb-1.5">ORDER SUMMARY</div>
                                    {addOnlineForm.items.map(i => (
                                        <div key={i.id} className="flex justify-between text-sm font-bold text-amber-900">
                                            <span>{i.qty}× {i.name}</span>
                                            <span>₹{(i.price_inr * i.qty).toFixed(0)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {addOnlineError && (
                                <div className="text-red-600 text-sm font-bold bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
                                    <i className="fas fa-exclamation-circle mr-2"></i>{addOnlineError}
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="px-5 pb-5 pt-3 border-t border-gray-100 flex-shrink-0 flex gap-3">
                            <button onClick={() => setShowAddOnlineModal(false)} className="flex-1 py-3 border-2 border-gray-200 rounded-xl text-gray-600 font-bold text-sm hover:bg-gray-50 transition-all">
                                Cancel
                            </button>
                            <button
                                onClick={submitManualOnlineOrder}
                                disabled={addOnlineLoading || addOnlineForm.items.length === 0}
                                className={`flex-1 py-3 rounded-xl font-black text-sm text-white transition-all flex items-center justify-center gap-2 ${
                                    addOnlineForm.platform === 'swiggy'
                                        ? 'bg-orange-500 hover:bg-orange-600 shadow-lg shadow-orange-200'
                                        : 'bg-red-600 hover:bg-red-700 shadow-lg shadow-red-200'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                {addOnlineLoading ? (
                                    <><i className="fas fa-spinner fa-spin"></i> Sending...</>
                                ) : (
                                    <><i className="fas fa-paper-plane"></i> Send to Kitchen</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* On-screen Toast Notification Popup */}
            {toast.show && (
                <div className={`fixed top-6 right-6 z-[9999] flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl animate-bounce-in text-white ${
                    toast.type === 'success' 
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-600 border border-emerald-400' 
                        : toast.type === 'warning'
                            ? 'bg-gradient-to-r from-amber-500 to-orange-600 border border-amber-400'
                            : 'bg-gradient-to-r from-blue-500 to-indigo-600 border border-blue-400'
                }`}>
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                        <i className={`fas ${
                            toast.type === 'success' 
                                ? 'fa-utensils' 
                                : toast.type === 'warning'
                                    ? 'fa-shopping-bag'
                                    : 'fa-info-circle'
                        } text-lg`}></i>
                    </div>
                    <div>
                        <div className="text-xs font-bold uppercase opacity-85">Kitchen Notification</div>
                        <div className="font-extrabold text-sm sm:text-base tracking-tight">{toast.message}</div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default KitchenPage;